import React from "react";
import * as d3 from "d3";
import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import { LineChartSettingsState } from "./LineChartSettings";
import WidgetLayout from "components/widgetParts/WidgetLayout";
import WidgetTitle from "components/widgetParts/WidgetTitle";
import WidgetLegend from "components/widgetParts/WidgetLegend";
import LineChart, { DatasetRecord, LineChartDataset } from "../chart/LineChart";
import line1 from "./mockedData/line1.json";
import line2 from "./mockedData/line2.json";
import { renderTrendEvent } from "../LineChartWidget";

interface LineChartWidgetPreviewProps {
  settings: LineChartSettingsState;
}

export default function LineChartWidgetPreview({ settings }: LineChartWidgetPreviewProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const colors = theme.colors.namedColors.chartColorArray;

  let mockedDatasets = [line1, line2];

  const datasets = mockedDatasets.map((mockedRecords, datasetIndex): LineChartDataset => {
    const color = colors[datasetIndex % colors.length];
    const values = mockedRecords.map((record) => record.y);

    const deviation = d3.deviation(values) ?? 1;
    const mean = d3.mean(values) ?? 0;
    
    const records = [];
    for (let i=0; i<mockedRecords.length; i++) {
      const stdDeviation = (mockedRecords[i].y - mean) / deviation;
      let showTrendEvent = settings.showTrendEvents && stdDeviation > 1;
      if (i > 0 && mockedRecords[i-1].y > mockedRecords[i].y) {
        // Don't show trend events when trending downward.
        showTrendEvent = false;
      }
      const data: DatasetRecord = {
        color,
        renderDot: showTrendEvent ? (props) => renderTrendEvent(props) : undefined,
        title: i18n.t("widget-types.line-chart.preview.line-title", {number: datasetIndex+1}),
        x: mockedRecords[i].x,
        y: mockedRecords[i].y
      };
      records.push(data);
    }
    const dataset: LineChartDataset = {
      color,
      records: records,
      xAxis: { dataKey: "x" },
      yAxis: { dataKey: "y" }
    }
    return dataset;
  });

  return (
    <WidgetLayout
      header={
        <WidgetTitle
          alignment={settings.widgetTitleAlignment}
          settingsTitle={settings.widgetTitleSource}
          defaultTitle={i18n.t("widget-types.line-chart.title")}
          customTitle={settings.widgetTitleCustom}
          widgetQueryTitle={i18n.t("query-title")}
        />
      }
    >
      <WidgetLegend
        colorGetter={(dataset) => dataset.color }
        data={datasets}
        horizontalAlign={settings.legendAlign?.horizontal}
        layout={settings.legendLayout}
        legendStyle={{ paddingBottom: 0 }}
        show={settings.legendShow}
        valueGetter={(dataset) => dataset.records[0].title}
        verticalAlign={settings.legendAlign?.vertical}
      >
        <LineChart
          alignStart={settings.alignStart}
          datasets={datasets}
          margin={{
            bottom: settings.showXAxis ? 5 : 0,
            right: settings.showYAxis ? 10 : 0,
            top: 10,
          }}
          showDots={settings.showDots}
          showXAxis={settings.showXAxis}
          showYAxis={settings.showYAxis}
          xTickInterval={settings.xTickInterval}
          yBaseline={settings.yBaseline}
          yTickInterval={settings.yTickInterval}
        />
      </WidgetLegend>
    </WidgetLayout>
  )
}